// =============================================================================
// src/chrome/topbar/index.ts — TopbarNav: markup + comportamento da topbar fixa.
//
// Markup: unified.html 83-117 (paridade total, MARKUP abaixo).
// Comportamento: porte FIEL de assets/unified-app.js `var TopbarNav` (2775-2972)
//   + os escutadores globais de popover (3219-3233).
//
// Diferenca do legado (sem global escondido): onde o TopbarNav chamava
//   GrafoRoute/WorkspaceRoute/ExploreRoute.open()/close() diretamente, o port
//   emite eventos no AppBus ('route:open'/'route:close'); os modulos de rota
//   (irmaos) escutam e fazem o toggle real. Cinema/Mapa passam pelo
//   SectionManager TS (core), exatamente como o legado fazia via SectionManager.
//   `syncActive` continua lendo as MESMAS body-classes do legado
//   (is-explore-view/is-grafo-view/is-workspace-view/cinema-active), entao os
//   pills ficam corretos independente de qual irmao ja montou.
//
// Timing preservado: scroll-to-top do feed com delay de 60ms (legado 2841-2843).
// =============================================================================

import type { AppContext, RouteName } from '../../app/types';
import { qsa, qs } from '../../app/dom';
import { TopbarPopoverController } from './topbar-popovers';
import { showMiniToast } from '../mini-toast/index';
import { downloadExploreCsv } from '../../app/export-explore-csv';
import { openSettingsPanel } from '../settings-panel/index';
import { signOutOperator } from '../../features/auth/index';
import { operatorInitials, buildGabineteChipMarkup } from './topbar-identity';
import { createWorkspaceStore } from '../../views/workspace/workspace-store';
import { showAnalyticsLoadingScreen } from './topbar-analytics-loading';
// per Context7 Vite asset docs (/vitejs/vite), 2026-06-26: ?url returns the bundled public URL.
import topbarLogoUrl from '../../../assets/logo.svg?url';

/** Markup da topbar (reforma 2026-06 F4: chip de gabinete + avatar derivado
 *  de topbar-identity — antes "TS" era hardcoded e a operacao nao tinha dono). */
const MARKUP = `<!-- ══════════════════════════════════════════════════════════════
     TOPBAR (fixed) — brand + chips (cidade + gabinete) à esquerda;
     busca + pill nav + overflow + avatar à direita.
     ══════════════════════════════════════════════════════════════ -->
<header class="u-topbar">
  <div class="u-topbar-left">
    <div class="brand" aria-label="Mapa político"><img class="u-topbar-logo" src="${topbarLogoUrl}" alt="" aria-hidden="true" /></div>
    <button type="button" id="crumbManaus" class="pill u-topbar-context" aria-label="Voltar ao mapa de Manaus">
      <i class="ti ti-map-pin"></i><span>Manaus</span>
    </button>
    ${buildGabineteChipMarkup()}
  </div>

  <div class="u-topbar-right">
    <button type="button" class="u-topbar-icon-btn u-topbar-search-btn" id="topbarSearch" title="Buscar (Ctrl+K)" aria-label="Buscar (Ctrl+K)">
      <i class="ti ti-search"></i>
    </button>

    <nav class="u-nav-pill-rail" role="tablist" aria-label="Superfícies do app">
      <button type="button" class="u-nav-pill is-active" data-view="briefing" role="tab" aria-selected="true">Briefing</button>
      <button type="button" class="u-nav-pill" data-view="explore" role="tab" aria-selected="false">Explorar</button>
      <button type="button" class="u-nav-pill is-disabled" data-view="mapa" role="tab" aria-selected="false" disabled aria-disabled="true" title="Feed do Mapa indisponível">Mapa</button>
      <button type="button" class="u-nav-pill is-disabled" data-view="grafo" role="tab" aria-selected="false" disabled aria-disabled="true" title="Grafo de Influências indisponível">Grafo</button>
      <button type="button" class="u-nav-pill is-disabled" data-view="workspace" role="tab" aria-selected="false" disabled aria-disabled="true" title="Workspace indisponível">Workspace</button>
      <button type="button" class="u-nav-pill is-disabled" data-view="cinema" role="tab" aria-selected="false" disabled aria-disabled="true" title="Modo Cinema indisponível">Cinema</button>
    </nav>

    <button type="button" class="u-topbar-analytics-btn" id="topbarAnalytics" title="Abrir Analytics" aria-label="Abrir Analytics">
      <i class="ti ti-chart-line" aria-hidden="true"></i><span>Analytics</span>
    </button>

    <button type="button" class="u-topbar-icon-btn u-topbar-overflow" id="topbarOverflow" title="Mais ações" aria-label="Mais ações" aria-haspopup="menu" aria-expanded="false">
      <i class="ti ti-dots"></i>
    </button>

    <button type="button" id="topbarAvatar" class="u-topbar-user" title="Conta" aria-label="Abrir menu da conta" aria-haspopup="menu" aria-expanded="false">
      <span class="u-topbar-user-circle">${operatorInitials()}</span>
      <i class="ti ti-chevron-down u-topbar-user-caret" aria-hidden="true"></i>
    </button>
  </div>
</header>`;

/** Devolve a string HTML literal deste bloco. */
export function buildTopbarMarkup(): string {
  return MARKUP;
}

/** Superficie do SectionManager TS que o TopbarNav aciona (cinema/mapa). */
interface SectionActivator {
  activateSection(sectionId: 'mapa-eleitoral' | 'cinema'): void;
}

// FEED_SCROLL_TOP_DELAY_MS (60ms, legado 2841-2843) saiu junto com goMapa() na
// desativacao do feed (2026-07-20) — so existia para o scroll-to-top do pill Mapa.
/** Ms ate scrollar para uma secao apos abrir o feed (usado pelo SearchPalette). */
const SECTION_SCROLL_DELAY_MS = 120;

/** As 6 superficies dos pills. `mapa` e o feed (nao e uma RouteName). */
type PillView = 'briefing' | 'explore' | 'mapa' | 'grafo' | 'workspace' | 'cinema';

const MOBILE_OVERFLOW_VIEW_BY_ACTION: Readonly<Partial<Record<string, PillView>>> = {
  'view-briefing': 'briefing',
  'view-explore': 'explore',
  'view-mapa': 'mapa',
  'view-grafo': 'grafo',
  'view-workspace': 'workspace',
  'view-cinema': 'cinema',
};

const MOBILE_OVERFLOW_SURFACE_BY_ACTION: Readonly<Partial<Record<string, string>>> = {
  'surface-crisis': '#crisisLauncher',
  'surface-copilot': '#copilotChatLauncher',
};

/**
 * Superficies DESATIVADAS: o pill e o item de menu continuam visiveis porem inertes.
 * Grafo de Influencias, Modo Cinema e feed Mapa (todos 2026-07-20) e Workspace
 * (2026-07-25, junto do pacote de ajustes do Analytics) — todos a pedido do usuario.
 * O atributo `disabled` do botao ja barra o clique real; este conjunto barra
 * as chamadas PROGRAMATICAS de go() (SearchPalette, popover mobile, menu da conta,
 * testes). O deep-link ?view=workspace foi fechado em landing-view.ts +
 * workspace-route.syncFromURL (mesma razao do grafo: URL nao pode ser porta dos
 * fundos de superficie fechada).
 *
 * ATENCAO ao 'mapa': diferente de grafo/cinema (superficies-folha), o feed era o HUB —
 * go('mapa') significava "feche tudo e volte ao feed". Desativa-lo torna as 8 secoes de
 * #contentCol (mapa-eleitoral ... monitoramento) inalcancaveis e deixa 3 chamadores em
 * no-op DELIBERADO, com o trade-off aceito explicitamente pelo usuario:
 *   · chip "Manaus" (bindChipManaus)          · scrollToSection (9 itens "Secoes" da palette)
 *   · SearchPalette acao 'region' (5 itens)   · ExploreUI.openDetail (drill-in do Explorar)
 * Para religar: remover a entrada daqui, restaurar goMapa() + o ramo em go() e reabilitar
 * o pill/o item mobile.
 */
const DISABLED_PILL_VIEWS: ReadonlySet<PillView> = new Set<PillView>(['grafo', 'cinema', 'mapa', 'workspace']);

/** Mapa body-class -> view, na ordem de prioridade do `syncActive` legado. */
const VIEW_BY_BODY_CLASS: readonly (readonly [className: string, view: PillView])[] = [
  ['is-briefing-view', 'briefing'],
  ['is-explore-view', 'explore'],
  ['is-grafo-view', 'grafo'],
  ['is-workspace-view', 'workspace'],
  ['cinema-active', 'cinema'],
];

const ANALYTICS_VIEW_CLASS = 'is-analytics-view';

/** Superficie minima de uma rota fullscreen, lida via window (publicada por main.ts/explore). */
interface RouteSingletonSurface {
  open(): void;
  close(): void;
  isOpen(): boolean;
}

/** Janela com os singletons de rota publicados (Briefing/Explore pelos inits das features; Grafo/Workspace por main.ts). */
interface RouteSingletonWindow {
  BriefingRoute?: RouteSingletonSurface;
  ExploreRoute?: RouteSingletonSurface;
  GrafoRoute?: RouteSingletonSurface;
  WorkspaceRoute?: RouteSingletonSurface;
  MapaIaRoute?: RouteSingletonSurface;
  AnalyticsRoute?: RouteSingletonSurface;
}

/**
 * Resolve o singleton de rota publicado no window pelo nome. Espelha as chamadas
 * diretas Route.open()/close() do TopbarNav legado — o port as trocou por eventos
 * de bus SEM receptor (nenhum modulo assinava route:open/close como COMANDO), o que
 * fazia os pills nao trocarem de view. Fix de paridade.
 */
function routeSingletonByName(route: RouteName): RouteSingletonSurface | undefined {
  const win = window as unknown as RouteSingletonWindow;
  if (route === 'briefing') return win.BriefingRoute;
  if (route === 'explore') return win.ExploreRoute;
  if (route === 'grafo') return win.GrafoRoute;
  if (route === 'mapa-ia') return win.MapaIaRoute;
  if (route === 'analytics') return win.AnalyticsRoute;
  return win.WorkspaceRoute;
}

/**
 * Controlador da pill nav + chip Manaus + popovers. Singleton exportado ao final
 * (TopbarNav), espelhando o `var TopbarNav` legado. `init(ctx)` recebe o AppBus
 * para coordenar rotas sem tocar nos irmaos.
 */
class TopbarNavController {
  private pills: HTMLElement[] = [];
  private pillByView = new Map<PillView, HTMLElement>();
  private context: AppContext | null = null;
  private sectionManager: SectionActivator | null = null;
  private readonly popovers = new TopbarPopoverController();

  /**
   * Liga pills, chip Manaus, popovers e a sincronia de pills via AppBus.
   * @param sectionManager Ativador de secao do core (cinema/mapa). Injetado por
   *   main.ts apos o SectionManager montar (DI por argumento, sem global).
   */
  init(ctx: AppContext, sectionManager: SectionActivator | null = null): void {
    this.context = ctx;
    this.sectionManager = sectionManager;
    this.indexPills();
    this.bindPillClicks();
    this.bindChipManaus();
    this.bindAnalyticsButton();
    this.wirePopovers();
    this.subscribeRouteSync(ctx);
    this.syncActive();
  }

  /** Indexa pills por data-view (substitui pillByView do legado). */
  private indexPills(): void {
    this.pills = qsa<HTMLElement>('.u-nav-pill');
    this.pillByView.clear();
    this.pills.forEach((pill) => {
      const view = pill.dataset.view as PillView | undefined;
      if (view) this.pillByView.set(view, pill);
    });
  }

  /** Cada pill navega para sua view ao clicar. */
  private bindPillClicks(): void {
    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const view = pill.dataset.view as PillView | undefined;
        if (view) this.go(view);
      });
    });
  }

  /** Chip "Manaus" sempre volta para o feed Mapa (legado 2792-2798). */
  private bindChipManaus(): void {
    const chip = qs<HTMLElement>('#crumbManaus');
    if (!chip) return;
    chip.addEventListener('click', (event) => {
      event.preventDefault();
      this.go('mapa');
    });
  }

  /** Botao destacado Analytics abre a rota fullscreen apos a tela de loading. */
  private bindAnalyticsButton(): void {
    const analyticsButtonEl = qs<HTMLElement>('#topbarAnalytics');
    if (!analyticsButtonEl) return;
    analyticsButtonEl.addEventListener('click', () => this.openAnalyticsPanel(analyticsButtonEl));
  }

  /** Exibe o loading e abre a rota Analytics quando o singleton estiver publicado. */
  private openAnalyticsPanel(analyticsButtonEl?: HTMLElement): void {
    const analyticsRoute = routeSingletonByName('analytics');
    if (!analyticsRoute) {
      showMiniToast('Analytics indisponivel', 'ti-chart-line');
      return;
    }
    const openAnalyticsRoute = (): void => this.emitRouteOpen('analytics');
    if (!analyticsButtonEl) {
      showAnalyticsLoadingScreen({ onComplete: openAnalyticsRoute });
      return;
    }
    showAnalyticsLoadingScreen({ sourceElement: analyticsButtonEl, onComplete: openAnalyticsRoute });
  }

  /** Liga os dois popovers (overflow + conta) + suas acoes + dismiss global. */
  private wirePopovers(): void {
    this.popovers.wire('topbarOverflow', 'topbarOverflowMenu');
    this.popovers.wire('topbarAvatar', 'topbarAccountMenu');
    this.wireOverflowActions();
    this.wireAccountActions();
    this.popovers.wireGlobalDismiss();
  }

  /**
   * Re-sincroniza os pills quando QUALQUER rota abre/fecha (inclusive via back
   * button dos irmaos). Substitui as chamadas TopbarNav.syncActive() que o
   * legado fazia dentro de cada Route.apply().
   */
  private subscribeRouteSync(ctx: AppContext): void {
    ctx.bus.on('route:open', () => this.syncActive());
    ctx.bus.on('route:close', () => this.syncActive());
    ctx.bus.on('section:change', () => this.syncActive());
  }

  /**
   * Navega para uma superficie. Porte do switch de TopbarNav.go (2819-2870),
   * preservando as diferencas POR-VIEW de saida do Cinema: explore/mapa saem via
   * SectionManager('mapa-eleitoral'); workspace remove `cinema-active` direto.
   * Por fim re-sincroniza pills.
   *
   * 2026-07-20: os ramos de ENTRADA de grafo e cinema sairam — ambas as superficies
   * estao desativadas e o guard DISABLED_PILL_VIEWS as intercepta antes do dispatch.
   * Os caminhos de SAIDA do Cinema continuam vivos (uma sessao antiga pode ter deixado
   * `cinema-active` no body). Ver DISABLED_PILL_VIEWS para religar.
   */
  go(view: PillView): void {
    if (DISABLED_PILL_VIEWS.has(view)) return;
    this.closeMapaIaIfOpen();
    this.closeAnalyticsIfOpen();
    if (view === 'briefing') this.goBriefing();
    else if (view === 'explore') this.goExplore();
    else this.goWorkspace();
    this.syncActive();
  }

  /** Pill Briefing (landing da reforma D2): fecha as irmas, sai do cinema, abre briefing. */
  private goBriefing(): void {
    this.emitRouteClose('explore');
    this.emitRouteClose('grafo');
    this.emitRouteClose('workspace');
    this.exitCinemaViaSectionManager();
    this.emitRouteOpen('briefing');
  }

  /** Pill Explorar: fecha briefing+grafo+workspace, sai do cinema via core, abre explore. */
  private goExplore(): void {
    this.emitRouteClose('briefing');
    this.emitRouteClose('grafo');
    this.emitRouteClose('workspace');
    this.exitCinemaViaSectionManager();
    this.emitRouteOpen('explore');
  }

  /** Pill Workspace: fecha briefing+explore+grafo, remove cinema-active direto, abre workspace. */
  private goWorkspace(): void {
    this.emitRouteClose('briefing');
    this.emitRouteClose('explore');
    this.emitRouteClose('grafo');
    this.removeCinemaClass();
    this.emitRouteOpen('workspace');
  }

  /** Sai do Cinema ativando 'mapa-eleitoral' no core (fallback: remove a body class). */
  private exitCinemaViaSectionManager(): void {
    if (!document.body.classList.contains('cinema-active')) return;
    if (this.sectionManager) this.sectionManager.activateSection('mapa-eleitoral');
    else this.removeCinemaClass();
  }

  /** Remove `cinema-active` direto (saida de cinema dos pills grafo/workspace). */
  private removeCinemaClass(): void {
    document.body.classList.remove('cinema-active');
  }

  /**
   * Abre a rota irma DIRETAMENTE (paridade com o legado TopbarNav.go, que chamava
   * Route.open()) e emite no bus para syncActive/observadores. O bus sozinho nao
   * abria a rota — ninguem assinava route:open como comando, so como notificacao.
   */
  private emitRouteOpen(route: RouteName): void {
    routeSingletonByName(route)?.open();
    this.context?.bus.emit('route:open', { route });
  }

  /** Fecha a rota irma diretamente (legado chamava Route.close()) + emite no bus. */
  private emitRouteClose(route: RouteName): void {
    routeSingletonByName(route)?.close();
    this.context?.bus.emit('route:close', { route });
  }

  /**
   * Sincroniza o pill ativo com o estado real (replica TopbarNav.syncActive,
   * 2877-2891): le as body-classes na MESMA ordem de prioridade do legado;
   * default = 'mapa'.
   */
  syncActive(): void {
    if (this.pillByView.size === 0) return;
    const activeView = this.detectActiveView();
    this.pillByView.forEach((pill, view) => {
      const isOn = view === activeView;
      pill.classList.toggle('is-active', isOn);
      pill.setAttribute('aria-selected', isOn ? 'true' : 'false');
    });
    this.syncAnalyticsButton();
  }

  /** Le as body-classes (ordem do legado) e devolve a view ativa, default 'mapa'. */
  private detectActiveView(): PillView | null {
    if (document.body.classList.contains(ANALYTICS_VIEW_CLASS)) return null;
    const match = VIEW_BY_BODY_CLASS.find(([className]) =>
      document.body.classList.contains(className),
    );
    return match ? match[1] : 'mapa';
  }

  /** Marca o CTA Analytics separadamente do rail de pills. */
  private syncAnalyticsButton(): void {
    const analyticsButtonEl = qs<HTMLElement>('#topbarAnalytics');
    if (!analyticsButtonEl) return;
    const isAnalyticsOpen = document.body.classList.contains(ANALYTICS_VIEW_CLASS);
    analyticsButtonEl.classList.toggle('is-active', isAnalyticsOpen);
    analyticsButtonEl.setAttribute('aria-pressed', isAnalyticsOpen ? 'true' : 'false');
  }

  /** Acoes do popover overflow (replica _wireOverflowActions, 2938-2954). */
  private wireOverflowActions(): void {
    const menu = qs<HTMLElement>('#topbarOverflowMenu');
    if (!menu) return;
    menu.addEventListener('click', (event) => this.onOverflowAction(event));
  }

  /**
   * Despacha a acao do item de overflow clicado.
   *
   * O listener e DELEGADO no #topbarOverflowMenu, entao itens `disabled` (acoes
   * desativadas em 2026-07-21) precisam de guard explicito: no navegador real um
   * botao desabilitado nao emite click, mas um dispatchEvent programatico — que e
   * como os testes clicam — ainda borbulha ate o menu e executaria a acao. Sem este
   * guard a suite daria VERDE FALSO sobre itens que o usuario nao consegue acionar.
   */
  private onOverflowAction(event: MouseEvent): void {
    const item = (event.target as HTMLElement).closest<HTMLElement>('.u-topbar-popover-item');
    if (!item) return;
    if (item instanceof HTMLButtonElement && item.disabled) return;
    this.popovers.close();
    this.runOverflowAction(item.dataset.action);
  }

  /**
   * Acoes do overflow — reforma 2026-06 (Fase 4): TODAS reais (antes 3 de 4
   * eram toasts mortos). download = CSV real do dataset; present = Cinema (o
   * modo apresentacao JA existia); bookmark = persiste analise no Workspace.
   */
  private runOverflowAction(action: string | undefined): void {
    if (!action) return;
    if (this.runMobileOverflowAction(action)) return;
    if (action === 'context') showMiniToast('Eleição Municipal 2024 ativa', 'ti-calendar-check');
    else if (action === 'bookmark') this.saveCurrentVista();
    else if (action === 'download') {
      const count = downloadExploreCsv();
      showMiniToast(`${count} municípios exportados (CSV)`, 'ti-download');
    } else if (action === 'present') this.go('cinema');
    else if (action === 'mapa-ia') this.emitRouteOpen('mapa-ia');
  }

  /** Acoes extras expostas apenas no drawer mobile do overflow. */
  private runMobileOverflowAction(action: string): boolean {
    const targetView = MOBILE_OVERFLOW_VIEW_BY_ACTION[action];
    if (targetView) {
      this.go(targetView);
      return true;
    }
    if (action === 'surface-analytics' || action === 'surface-reputation') {
      this.openAnalyticsPanel();
      return true;
    }
    return this.clickMobileSurfaceLauncher(action);
  }

  /** Aciona launchers que continuam no DOM, mas ficam ocultos no mobile. */
  private clickMobileSurfaceLauncher(action: string): boolean {
    const launcherSelector = MOBILE_OVERFLOW_SURFACE_BY_ACTION[action];
    if (!launcherSelector) return false;
    qs<HTMLElement>(launcherSelector)?.click();
    return true;
  }

  /** Fecha o Mapa de IA (rota meta sem pill) ao navegar para qualquer superfície. */
  private closeMapaIaIfOpen(): void {
    const mapaIa = routeSingletonByName('mapa-ia');
    if (mapaIa?.isOpen()) mapaIa.close();
  }

  /** Fecha Analytics ao voltar para qualquer superficie principal dos pills. */
  private closeAnalyticsIfOpen(): void {
    const analytics = routeSingletonByName('analytics');
    if (analytics?.isOpen()) analytics.close();
  }

  /** Persiste a vista atual como analise REAL no Workspace (localStorage). */
  private saveCurrentVista(): void {
    const view = this.detectActiveView() ?? 'mapa';
    const store = createWorkspaceStore({ now: () => Date.now(), storage: localStorage });
    store.createAnalysis({ title: `Vista salva — ${VIEW_LABEL[view]}`, tags: [], targetSection: 'mapa-eleitoral' });
    showMiniToast('Vista salva no Workspace', 'ti-bookmark-filled');
  }

  /** Acoes do popover de conta (replica _wireAccountActions, 2956-2971). */
  private wireAccountActions(): void {
    const menu = qs<HTMLElement>('#topbarAccountMenu');
    if (!menu) return;
    menu.addEventListener('click', (event) => this.onAccountAction(event));
  }

  /** Despacha a acao do item de conta clicado (workspace abre via go). */
  private onAccountAction(event: MouseEvent): void {
    const item = (event.target as HTMLElement).closest<HTMLElement>('.u-topbar-popover-item');
    if (!item) return;
    this.popovers.close();
    this.runAccountAction(item.dataset.action);
  }

  /** Mapeia a acao de conta. Reforma 2026-06 (F4): settings abre o painel
   *  REAL (chrome/settings-panel) — o "em breve" era tell de prototipo.
   *  2026-07: signout tambem virou real (features/auth) — antes era so toast. */
  private runAccountAction(action: string | undefined): void {
    if (action === 'workspace') {
      this.go('workspace');
      return;
    }
    if (action === 'settings') openSettingsPanel();
    else if (action === 'signout') this.signOutSession();
  }

  /** Sair: limpa a sessao mock persistida e reapresenta a tela de login por cima
   *  do app montado (signOutOperator NAO re-bootstrapa; ver features/auth). */
  private signOutSession(): void {
    signOutOperator();
    showMiniToast('Sessão encerrada', 'ti-logout');
  }

  /**
   * Rola ate uma secao do feed apos garantir o Mapa aberto. Usado pelo
   * SearchPalette (porte de activate -> TopbarNav.go('mapa') + scrollIntoView).
   */
  scrollToSection(sectionId: string): void {
    this.go('mapa');
    setTimeout(() => {
      qs(`#section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, SECTION_SCROLL_DELAY_MS);
  }
}

/** Rotulo legivel de cada view dos pills (usado no titulo da vista salva). */
const VIEW_LABEL: Record<PillView, string> = {
  briefing: 'Briefing',
  explore: 'Explorar',
  mapa: 'Mapa',
  grafo: 'Grafo',
  workspace: 'Workspace',
  cinema: 'Cinema',
};

/**
 * Singleton do TopbarNav — substitui o `var TopbarNav` legado. main.ts chama
 * TopbarNav.init(ctx, sectionManager) apos montar a topbar e o SectionManager.
 */
export const TopbarNav = new TopbarNavController();
